import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../Config/api";
import { FAST_API_URL } from "../Config/fast_api";
import { SOCKET_URL } from "../Config/ws";
import { SOCKET_URL_CALL } from "../Config/GrandStreamApi/fast_api";

const CallContext = createContext({});
export const useCall = () => useContext(CallContext);

export const CallProvider = ({ children }) => {
  const [incomingCall, setIncomingCall] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showSideDialpad, setShowSideDialpad] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const socketRef = useRef(null);
  const heartbeatInterval = useRef(null);
  const reconnectTimeout = useRef(null);
  
  // 1. REJECT FIX: Yeh Set un calls ka ID rakhega jo user ne manually reject ki hain
  const rejectedCallsRef = useRef(new Set());

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    const checkAuth = setInterval(() => {
      const token = localStorage.getItem("token");
      if (!token && isModalOpen) {
        setIsModalOpen(false);
        setIncomingCall(null);
        if (socketRef.current) socketRef.current.close();
      }
    }, 2000);

    return () => clearInterval(checkAuth);
  }, [isModalOpen]);

  const disconnectSocket = useCallback(() => {
    console.log("🛑 Manually Disconnecting Socket...");
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }
    if (socketRef.current) {
      socketRef.current.onclose = null;
      socketRef.current.close();
      socketRef.current = null;
    }
    clearInterval(heartbeatInterval.current);
    setIncomingCall(null);
    setIsModalOpen(false);
    rejectedCallsRef.current.clear(); // Clear reject memory on disconnect
  }, []);

  const handleCreateCallApi = async (callData, statusOverride = null) => {
    try {
      const savedExt = localStorage.getItem("selected_extension");
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("clg_id");

      const finalStatus = statusOverride || callData.status;

      const payload = {
        uniqueid: callData.uniqueid,
        linkedid: callData.linkedid || callData.uniqueid,
        src: callData.src,
        dst: callData.dst,
        status: finalStatus,
        extension: savedExt,
        clg_ref_id: userId ? parseInt(userId, 10) : null,
      };

      await axios.post(`${SOCKET_URL_CALL}/create-call`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      setIncomingCall((prev) => prev ? { ...prev, status: finalStatus } : null);
    } catch (error) {
      console.error(" Error hitting Create Call API:", error);
    }
  };

  // 2. REJECT FUNCTION: Ise component mein expose karenge
  const handleRejectLocally = useCallback((uniqueid) => {
    if (uniqueid) {
      rejectedCallsRef.current.add(uniqueid); // ID blacklist mein daal di
    }
    setIsModalOpen(false);
    setIncomingCall(null);
  }, []);

  const connectSocket = useCallback((extension) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) return;
    if (!extension) return;

    const wsUrl = `${SOCKET_URL_CALL}/fbackend/ws/calls?exten=${extension}`;
    // const wsUrl = `${SOCKET_URL_CALL}/ws/calls?exten=${extension}`;
    const socket = new WebSocket(wsUrl);
    socketRef.current = socket;   

    socket.onopen = () => {
      console.log("✅ WebSocket Connected for Ext:", extension);
      heartbeatInterval.current = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: "ping" }));
        }
      }, 30000);
    };

    socket.onmessage = (event) => {
      try {
        const response = JSON.parse(event.data);
        const savedExt = localStorage.getItem("selected_extension");

        if (response.event === "ringing") {
          const { src, dst, uniqueid } = response.data;

          // 3. REJECT FIX: Agar call rejected list mein hai, toh uska ringing event ignore karo
          if (rejectedCallsRef.current.has(uniqueid)) {
            console.log(`🚫 Ignored ringing event for manually rejected call: ${uniqueid}`);
            return;
          }

          if (src === dst) {
            if (savedExt && src === savedExt) {
              showSnackbar(`Extension ${savedExt} is logged in.`, "success");
            }
            return;
          }

          setIncomingCall({ ...response.data, status: "RINGING" });
          setTimeout(() => {
            setIncomingCall((currentCall) => {
              if (currentCall && currentCall.status === "RINGING") {
                setIsModalOpen(true);
              }
              return currentCall;
            });
          }, 500);
        }

        else if (response.event === "answered") {
          const answeredData = { ...response.data, status: "ANSWERED" };
          setIncomingCall((prev) => ({ ...prev, ...answeredData }));
          handleCreateCallApi(answeredData);
        }

        else if (response.event === "hangup") {
          setIncomingCall((prev) => {
            // 4. RACE CONDITION FIX: ID match check
            if (prev && prev.uniqueid && prev.uniqueid !== response.data.uniqueid) {
              console.log("⚠️ Ignored hangup of background call. Keeping active call safe.");
              return prev; 
            }

            // Agar ye wahi call hai jo screen par dikh rahi hai, tabhi timer lagao
            setTimeout(() => {
              setIncomingCall((currentCallState) => {
                // Timer khatam hone par wapas ID check karo, taaki naya call close na ho jaye
                if (currentCallState && currentCallState.uniqueid === response.data.uniqueid) {
                  setIsModalOpen(false);
                  return null;
                }
                return currentCallState;
              });
            }, 4000);

            return {
              ...prev,
              ...response.data,
              status: "COMPLETED",
            };
          });
        }
      } catch (err) {
        console.error("Socket parse error:", err);
      }
    };

    socket.onclose = () => {
      socketRef.current = null;
      clearInterval(heartbeatInterval.current);
      const token = localStorage.getItem("token");
      const extension = localStorage.getItem("selected_extension");

      if (token && extension) {
        reconnectTimeout.current = setTimeout(() => {
          connectSocket(extension);
        }, 5000);
      } else {
        setIsModalOpen(false);
        setIncomingCall(null);
      }
    };

    socket.onerror = (err) => {
      socket.close();
    };
  }, []);

  useEffect(() => {
    const checkAuthStatus = () => {
      const token = localStorage.getItem("token");
      const extension = localStorage.getItem("selected_extension");
      if (token && extension) {
        if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
          connectSocket(extension);
        }
      } else {
        disconnectSocket();
      }
    };

    checkAuthStatus();
    const authInterval = setInterval(checkAuthStatus, 2000);
    return () => {
      clearInterval(authInterval);
      disconnectSocket();
    };
  }, [connectSocket, disconnectSocket]);

  const sendDTMF = async (digit) => {
    try {
      const savedExt = localStorage.getItem("selected_extension");
      const payload = {
        extension: savedExt,
        uniqueid: incomingCall?.uniqueid,
        digit: digit,
      };
      await axios.post(`${SOCKET_URL_CALL}/send-dtmf`, payload);
    } catch (error) {
      console.error("Error sending DTMF:", error);
    }
  };

  return (
    <CallContext.Provider
      value={{
        incomingCall,
        isModalOpen,
        connectSocket,
        setIsModalOpen,
        isMinimized,
        setIsMinimized,
        snackbar,
        setSnackbar,
        showSnackbar,
        disconnectSocket,
        handleCreateCallApi,
        showSideDialpad,
        setShowSideDialpad,
        sendDTMF,
        handleRejectLocally, 
      }}
    >
      {children}
    </CallContext.Provider>
  );
};